import React, { useEffect } from 'react'
import { observer, inject } from 'mobx-react'
import {
  ActivityHistoryWrapper,
  ActivityItem,
  TableWrapper,
  TableCell,
} from './ActivityHistoryStyled'

const formatDate = dateString => {
  const date = new Date(dateString)
  const hours = date.getHours().toString().padStart(2, '0')
  const minutes = date.getMinutes().toString().padStart(2, '0')
  const day = date.getDate().toString().padStart(2, '0')
  const month = (date.getMonth() + 1).toString().padStart(2, '0')
  const year = date.getFullYear()
  return `${hours}:${minutes} - ${day}/${month}/${year}`
}

const translateUrgencyLevel = urgencyLevel => {
  switch (urgencyLevel) {
    case '001':
      return 'Khẩn'
    case '002':
      return 'Hỏa tốc'
    case '003':
      return 'Hỏa tốc hẹn giờ'
    default:
      return 'Không xác định'
  }
}

const formatPermission = permission => {
  switch (permission) {
    case 'VIEW':
      return 'Phối hợp / Theo dõi'
    case 'PIC':
      return 'Lãnh đạo xử lý'
    case 'COOR':
      return 'Người xử lý'
    default:
      return permission
  }
}

const capitalizeFirstLetters = str => {
  if (typeof str !== 'string') return ''
  return str
    .toLowerCase()
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
}

const extractFieldChanges = (dataBefore, dataAfter) => {
  const changes = []
  try {
    const before =
      typeof dataBefore === 'string' ? JSON.parse(dataBefore) : dataBefore
    const after =
      typeof dataAfter === 'string' ? JSON.parse(dataAfter) : dataAfter

    if (!before && !after) return changes

    const oldPersons =
      before?.old_persons && Array.isArray(before.old_persons)
        ? before.old_persons
        : []

    const addedPersons =
      after?.add_persons && Array.isArray(after.add_persons)
        ? after.add_persons
        : []

    const removedPersons =
      after?.remove_persons && Array.isArray(after.remove_persons)
        ? after.remove_persons
        : []

    const formatPerson = person =>
      `${formatPermission(person.permission)}: ${capitalizeFirstLetters(
        person.nameUppercase || ''
      )}`

    const oldPersonsHtml = oldPersons
      .map(person => `<div>${formatPerson(person)}</div>`)
      .join('')

    const removedHtml = removedPersons
      .map(
        p =>
          `<div style="color: #ef4444; text-decoration: line-through;">- ${formatPerson(
            p
          )}</div>`
      )
      .join('')

    const addedHtml = addedPersons
      .map(p => `<div style="color: #0000FF;">+ ${formatPerson(p)}</div>`)
      .join('')

    if (addedPersons.length > 0 || removedPersons.length > 0) {
      changes.push({
        field: 'Xử lý văn bản',
        before: oldPersonsHtml || 'Không có dữ liệu',
        after: `${addedHtml}${removedHtml}` || '',
        isHtml: true,
      })
    }

    if (after?.document_dates) {
      const { startDate, endDate } = after.document_dates

      if (startDate && !startDate.unchanged) {
        const beforeDate = before?.document_dates?.startDate
          ? formatDateFromObject(before.document_dates.startDate)
          : ''
        const afterDate = startDate.value
          ? formatDateFromObject(startDate.value)
          : ''
        if (beforeDate || afterDate) {
          changes.push({
            field: 'Ngày bắt đầu',
            before: beforeDate,
            after: afterDate,
          })
        }
      }

      if (endDate && !endDate.unchanged) {
        const beforeDate = before?.document_dates?.endDate
          ? formatDateFromObject(before.document_dates.endDate)
          : ''
        const afterDate = endDate.value
          ? formatDateFromObject(endDate.value)
          : ''
        if (beforeDate || afterDate) {
          changes.push({
            field: 'Ngày kết thúc',
            before: beforeDate,
            after: afterDate,
          })
        }
      }
    }

    if (after?.document_status) {
      const statusMap = {
        PENDING: 'Chưa xử lý',
        INPROGRESS: 'Đã bút phê',
        COMPLETE: 'Hoàn thành',
      }
      const beforeStatus = before?.document_status
        ? statusMap[before.document_status] || before.document_status
        : ''
      const afterStatus =
        statusMap[after.document_status] || after.document_status
      changes.push({
        field: 'Trạng thái văn bản',
        before: beforeStatus,
        after: afterStatus,
      })
    }

    if (after?.old_persons && typeof after.old_persons === 'object') {
      const docData = after.old_persons
      const beforeDocData = before?.remove_persons || {}

      const fieldMappings = {
        bookGroupName: 'Nhóm số văn bản',
        bookName: 'Số văn bản',
        documentNumber: 'Số hiệu',
        incomingNumber: 'Số đến',
        title: 'Trích yếu',
        signer: 'Người ký',
        authorityIssuedName: 'Cơ quan ban hành',
      }

      Object.entries(fieldMappings).forEach(([key, label]) => {
        if (docData[key] !== undefined) {
          changes.push({
            field: label,
            before: beforeDocData[key] || '',
            after: docData[key] || '',
          })
        }
      })

      if (docData.outgoingDocumentDate?.dateTime?.date) {
        const { year, month, day } = docData.outgoingDocumentDate.dateTime.date
        const beforeDate = beforeDocData.outgoingDocumentDate
          ? formatDateFromObject(beforeDocData.outgoingDocumentDate)
          : ''

        changes.push({
          field: 'Ngày đến',
          before: beforeDate,
          after: formatDateFromObject(docData.outgoingDocumentDate),
        })
      }

      const afterDateIssued = docData.dateIssued
      const beforeDateIssued = beforeDocData.dateIssued

      const beforeDateIssuedStr = beforeDateIssued
        ? formatDateFromObject(beforeDateIssued)
        : ''
      const afterDateIssuedStr = afterDateIssued
        ? formatDateFromObject(afterDateIssued)
        : ''

      if (beforeDateIssuedStr || afterDateIssuedStr) {
        if (beforeDateIssuedStr !== afterDateIssuedStr) {
          changes.push({
            field: 'Ngày văn bản',
            before: beforeDateIssuedStr,
            after: afterDateIssuedStr,
          })
        }
      }

      const beforeUrgency = beforeDocData.urgencyLevel
        ? translateUrgencyLevel(beforeDocData.urgencyLevel)
        : ''
      const afterUrgency = docData.urgencyLevel
        ? translateUrgencyLevel(docData.urgencyLevel)
        : ''

      if (beforeUrgency !== afterUrgency) {
        changes.push({
          field: 'Độ khẩn',
          before: beforeUrgency,
          after: afterUrgency,
        })
      }

      if (docData.attachmentChanges?.length > 0) {
        const addedFiles = docData.attachmentChanges
          .filter(change => change.startsWith('Thêm tệp:'))
          .map(file => file.replace('Thêm tệp: ', ''))
        const removedFiles = docData.attachmentChanges
          .filter(change => change.startsWith('Bỏ tệp:'))
          .map(file => file.replace('Bỏ tệp: ', ''))

        const currentAttachments =
          beforeDocData.attachments || docData.attachments || []
        const currentAttachmentsList = Array.isArray(currentAttachments)
          ? currentAttachments.map(file => `<div>${file}</div>`).join('')
          : `<div>${currentAttachments}</div>`

        let changesHtml = ''

        if (addedFiles.length > 0) {
          const addedFilesHtml = addedFiles
            .map(file => `<div style="color: #0000FF;">+ ${file}</div>`)
            .join('')
          changesHtml += addedFilesHtml
        }

        if (removedFiles.length > 0) {
          const removedFilesHtml = removedFiles
            .map(
              file =>
                `<div style="color: #ef4444; text-decoration: line-through;">- ${file}</div>`
            )
            .join('')
          changesHtml += removedFilesHtml
        }

        if (addedFiles.length > 0 || removedFiles.length > 0) {
          changes.push({
            field: 'Tài liệu đính kèm',
            before: currentAttachmentsList || 'Không có tài liệu',
            after: changesHtml,
            isHtml: true,
          })
        }
      }
    }
  } catch (e) {
    console.error('Error parsing data:', e)
  }

  return changes
}

const formatDateFromObject = dateObj => {
  if (!dateObj?.dateTime?.date) return ''
  const { year, month, day } = dateObj.dateTime.date

  if (
    year < 1900 ||
    year > 2100 ||
    month < 1 ||
    month > 12 ||
    day < 1 ||
    day > 31
  )
    return ''

  const paddedDay = day.toString().padStart(2, '0')
  const paddedMonth = month.toString().padStart(2, '0')

  return `${paddedDay}/${paddedMonth}/${year}`
}

const ActivityHistory = ({ listHistoryLogStore, documentCode }) => {
  const { historyLists, getLists, isLoading } = listHistoryLogStore

  useEffect(() => {
    if (documentCode) {
      getLists(documentCode)
    }
  }, [documentCode])

  const renderActivityItem = (activity, index) => {
    const userUppercase = activity.user.name_uppercase || ''
    const formattedUserUppercase = capitalizeFirstLetters(userUppercase)

    const actionText = (() => {
      switch (activity.action) {
        case 'CREATE':
          return 'Tạo'
        case 'UPDATE':
        case 'EDIT_ASSIGNEE':
          return 'Chỉnh sửa'
        case 'DELETE':
          return 'Xóa'
        case 'RESTORE':
          return 'Khôi phục'
        default:
          return ''
      }
    })()

    if (!actionText) return null

    const fieldChanges = extractFieldChanges(
      activity.data_before,
      activity.data_after
    )

    const alwaysVisibleActions = ['CREATE', 'RESTORE', 'DELETE']

    if (
      fieldChanges.length === 0 &&
      !alwaysVisibleActions.includes(activity.action)
    ) {
      return null
    }

    return (
      <ActivityItem key={index}>
        <ul>
          <li>
            <p>
              <b>{actionText}</b> lúc {formatDate(activity.processed_date)} bởi{' '}
              <b>{formattedUserUppercase}</b>
            </p>
          </li>
        </ul>
        {(fieldChanges.length > 0 ||
          ['CREATE', 'RESTORE', 'DELETE'].includes(activity.action)) && (
          <TableWrapper>
            <TableCell className="title">Trường dữ liệu</TableCell>
            <TableCell className="title">Trước thay đổi</TableCell>
            <TableCell className="title">Sau thay đổi</TableCell>
            {fieldChanges.length === 0 ? (
              <React.Fragment>
                <TableCell
                  colSpan={3}
                  style={{ textAlign: 'center', padding: '10px' }}></TableCell>
              </React.Fragment>
            ) : (
              fieldChanges.map((change, changeIndex) => {
                const rowStyle = {
                  backgroundColor:
                    changeIndex % 2 === 0 ? '#ffffff' : '#e6f0ff',
                  whiteSpace: 'pre-line',
                }
                return (
                  <React.Fragment key={changeIndex}>
                    <TableCell style={rowStyle}>{change.field}</TableCell>
                    <TableCell style={rowStyle}>
                      {change.isHtml ? (
                        <div
                          dangerouslySetInnerHTML={{ __html: change.before }}
                        />
                      ) : (
                        change.before || 'Chưa có dữ liệu'
                      )}
                    </TableCell>
                    <TableCell style={rowStyle}>
                      {change.isHtml ? (
                        <div
                          dangerouslySetInnerHTML={{ __html: change.after }}
                        />
                      ) : (
                        change.after || 'Chưa có dữ liệu'
                      )}
                    </TableCell>
                  </React.Fragment>
                )
              })
            )}
          </TableWrapper>
        )}
      </ActivityItem>
    )
  }

  const validActivities =
    historyLists?.filter(activity => {
      const fieldChanges = extractFieldChanges(
        activity.data_before,
        activity.data_after
      )

      const alwaysShowActions = ['CREATE', 'RESTORE', 'DELETE']

      return (
        fieldChanges.length > 0 || alwaysShowActions.includes(activity.action)
      )
    }) || []

  return (
    <ActivityHistoryWrapper>
      {isLoading ? (
        <p>Đang tải...</p>
      ) : validActivities.length === 0 ? (
        <p>Không có lịch sử hoạt động.</p>
      ) : (
        <div className="activity-list">
          {validActivities
            .sort((a, b) => {
              const dateA = new Date(a.processed_date).getTime()
              const dateB = new Date(b.processed_date).getTime()
              return dateB - dateA
            })
            .map(renderActivityItem)}
        </div>
      )}
    </ActivityHistoryWrapper>
  )
}

export default inject('listHistoryLogStore')(observer(ActivityHistory))
